import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import Checbox from '@8x8/oxygen-checkbox';
import CheckIcon from '@8x8/oxygen-icon/icons/CheckIcon';
import UserIcon from '@8x8/oxygen-icon/icons/UserIcon';
import EyeSlashIcon from '@8x8/oxygen-icon/icons/EyeSlashIcon';
import EyeIcon from '@8x8/oxygen-icon/icons/EyeIcon';
import EnvelopeIcon from '@8x8/oxygen-icon/icons/EnvelopeIcon';
import LockIcon from '@8x8/oxygen-icon/icons/LockIcon';
import ArrowLeftSmallIcon from '@8x8/oxygen-icon/icons/ArrowLeftSmallIcon';
import Tooltip from '@8x8/oxygen-tooltip';

import StyledInput from './basic/styled-input';
import Button from './basic/button';
import { focusInput, validateEmail, validatePassword } from '../core/utils';
import auth0, { isVpaaS, prefillEmail } from '~/services/auth0';
import { translate } from '~/services/translate';
import { EMAIL_PASSWORD_CONNECTION_NAME, MOBILE_THRESHOLD } from '~/core/constants';
import analytics from '~/services/amplitude';
import { Flex, FlexCentered } from '~/components/basic/containers';

const PANE_TO_RENDER = {
  EMAIL: 'email',
  PASSWORD: 'password',
}

const BackToMainScreenContainer = styled(FlexCentered)`
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: ${({ theme }) => theme.lightBlue};
  justify-content: center;
  cursor: pointer;
  @media screen and (max-width: ${MOBILE_THRESHOLD}px) {
    line-height: 48px;
  }
`;

const PasswordContainer = styled(Flex)`
  position: relative;
`;

const RightIconContainer = styled(FlexCentered)`
  width: 32px;
  position: absolute;
  right: 0;
  height: 40px;
  cursor: pointer;

  @media screen and (max-width: ${MOBILE_THRESHOLD}px) {
    height: 48px;
  }
`;

const OptInLabel = styled.div`
  font-size: 8px;
  margin-left: 10px;
  margin-top: 6px;
`;

const OptInContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
  align-items: flex-start;
`;

const StyledAnchor = styled.a.attrs({
  rel: 'noopener noreferrer',
  target:'_blank',
})`
  text-decoration: none;
`

const EmailPane = ({ isSignUp, shouldHideProviders }) => {
  const theme = useContext(ThemeContext);
  const receivedEmail = prefillEmail();
  const [emailAddress, setEmailAddress] = useState(receivedEmail);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [optin, setOptin] = useState(isVpaaS() ? true : undefined);
  const [continueDisabled, setContinueDisabled] = useState(!validateEmail(receivedEmail));
  const [paneToRender, setPaneToRender] = useState(PANE_TO_RENDER.EMAIL);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const nameInputRef = useRef(null);
  const pwdInputRef = useRef(null);

  const handleKeyDown = useCallback(event => {
    const { keyCode } = event;
    if (keyCode !== 13) {
      return;
    }

    if (!continueDisabled) {
      if (paneToRender === PANE_TO_RENDER.EMAIL) {
        onConfirmEmail();
      }
      if (paneToRender === PANE_TO_RENDER.PASSWORD) {
        onConfirmPassword();
      }
    }
  }, [continueDisabled, paneToRender, emailAddress, password, inProgress]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown]);

  useEffect(() => {
    if (paneToRender === PANE_TO_RENDER.PASSWORD) {
      focusInput(pwdInputRef);
    }
  }, [continueDisabled]);

  const onConfirmEmail = () => {
    analytics.trackPageView(analytics.PAGE_NAMES.PASSWORD);
    setPaneToRender(PANE_TO_RENDER.PASSWORD);
    setContinueDisabled(isSignUp);
    shouldHideProviders(true);
    focusInput(nameInputRef);
  }

  const onGoBack = () => {
    setPaneToRender(PANE_TO_RENDER.EMAIL);
    shouldHideProviders(false);
    setContinueDisabled(!validateEmail(emailAddress));
  }

  const onConfirmPassword = () => {
    if (inProgress) {
      return;
    }

    setInProgress(true);
    analytics.trackEvent(analytics.EVENT_NAMES.AUTH, {
      connectionName: EMAIL_PASSWORD_CONNECTION_NAME,
    });
    auth0.continueWithUserPassword(emailAddress, password, name, optin).then(() => {
      setInProgress(false);
    });
  }

  const handleEmailChange = event => {
    const email = event.target.value;
    setEmailAddress(email);
    setContinueDisabled(!validateEmail(email));
  };

  const handlePasswordChange = event => {
    const pwd = event.target.value;
    setPassword(pwd);
    setContinueDisabled(isSignUp && !validatePassword(pwd));
  };

  const handleNameChanged = event => {
    setName(event.target.value);
  };

  const handleOptinChange = () => {
    setOptin(!optin);
  }

  const renderEmailPane = () => (
    <>
      <StyledInput
        iconLeft={ EnvelopeIcon }
        type="email"
        placeholder="email@example.com"
        onChange={ handleEmailChange }
        value={ emailAddress }
      />
      <Button
        data-test-id="button-email-continue"
        backgroundColor={ theme.lightBlue }
        backgroundColorHover="#278ADF"
        callback={ onConfirmEmail }
        text={ translate('continue') }
        disabled={ continueDisabled }
      />
    </>
  );

  const showHidePassword = () => {
    setPasswordVisible(!passwordVisible);
  }

  const renderPasswordContainer = () => {
    const PasswordWrapper = continueDisabled ? Tooltip : React.Fragment;
    const passwordWrapperProps = continueDisabled ? { 
        delay: 300,
        title: translate('password_validation_tooltip'),
        orientation: 'top',
        showOn: 'click;',
      } : {};

    return (
      <>
        { isSignUp && (
          <StyledInput
            iconLeft={ UserIcon }
            type="text"
            placeholder="First name Last name"
            onChange={ handleNameChanged }
            value={ name }
            ref={ nameInputRef }
          />
        ) }
        <PasswordWrapper {...passwordWrapperProps} >
          <PasswordContainer>
            <StyledInput
              type={ passwordVisible ? 'text' : 'password' }
              placeholder="Enter your password here"
              onChange={ handlePasswordChange }
              iconLeft={ LockIcon }
              iconRight={ RightIconContainer }
              value={ password }
              ref={ pwdInputRef }
            />
            <RightIconContainer onClick={ showHidePassword }>
              { passwordVisible ? <EyeSlashIcon color="#5E6D7A" /> : <EyeIcon color="#5E6D7A" /> }
            </RightIconContainer>
          </PasswordContainer>
        </PasswordWrapper>
        { isSignUp && isVpaaS() && (
          <OptInContainer>
            <Checbox
              value={ optin }
              isChecked={ optin }
              onChange={ handleOptinChange }
              theme={{
                checkboxSize: '10px',
              }}
            />
            <OptInLabel>
              8x8 needs the contact information you provide to us to contact you about our products and services. You may unsubscribe from these communications at any time. For information on how to unsubscribe, as well as our privacy practices and commitment to protecting your privacy, please review our&nbsp;
              <StyledAnchor
                href="https://www.8x8.com/terms-and-conditions/privacy-policy"
              >Privacy policy</StyledAnchor>
            </OptInLabel>
          </OptInContainer>
        ) }
      </>
    )
  }

  const renderPasswordPane = () => (
    <>
      <StyledInput
        value={ emailAddress }
        isDisabled
        iconLeft={ CheckIcon }
        theme={ {
          colorIcon: '#31B76A',
          colorDisabled: '#1C2025',
        } } />
      { renderPasswordContainer() }
      <Button
        data-test-id="button-password-continue"
        backgroundColor={ theme.lightBlue }
        backgroundColorHover="#278ADF"
        callback={ onConfirmPassword }
        text={ translate('continue') }
        disabled={ continueDisabled || inProgress }
      />
      <BackToMainScreenContainer onClick={ onGoBack }>
        <ArrowLeftSmallIcon color={ theme.lightBlue } />
        <span>{ isSignUp ?
          translate('all_signup_options') :
          translate('all_login_options') }
        </span>
      </BackToMainScreenContainer>
    </>
  );

  switch (paneToRender) {
    case PANE_TO_RENDER.EMAIL:
      return renderEmailPane();
    case PANE_TO_RENDER.PASSWORD:
      return renderPasswordPane();
    default:
      return null;
  }
}

EmailPane.propTypes = {
  shouldHideProviders: PropTypes.func,
  isSignUp: PropTypes.bool,
}

export default EmailPane;